import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@shared/utils/utils';
import { Calendar } from '@shared/components/ui/shadcn/calendar';
import { Button } from '../button/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { MonthPicker } from './MonthPicker';

interface DatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  id?: string;
  error?: boolean;
  mode?: 'single' | 'month';
  formatDisplay?: string;
}

function DatePicker({
  date,
  onDateChange,
  placeholder = 'Seleccionar fecha',
  disabled = false,
  className,
  'data-testid': testId,
  id,
  error = false,
  mode = 'single',
  formatDisplay,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  if (mode === 'month') {
    return (
      <MonthPicker
        date={date}
        onDateChange={onDateChange}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        data-testid={testId}
        id={id}
        error={error}
      />
    );
  }

  const handleDateSelect = (selectedDate: Date | undefined) => {
    onDateChange?.(selectedDate);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal h-10 px-3 py-2 bg-card-bg border-card-border',
            !date && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
            className,
          )}
          disabled={disabled}
          data-testid={testId}
          id={id}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, formatDisplay || 'PPP', { locale: es }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={handleDateSelect} captionLayout="label" />
      </PopoverContent>
    </Popover>
  );
}
DatePicker.displayName = 'DatePicker';

export { DatePicker, type DatePickerProps };
